// @flow
import * as React from 'react';
import styled from 'styled-components';
import type { ImageData } from 'api/shared';
import { imageUrl } from 'utils/filestack';
import { H2, Body } from 'components/Text';
import Form, { InputField, SubmitButton } from 'components/Form';
import Flex from 'components/Flex';
import Box from 'components/Box';
import { colors, gutterWidth } from '../../theme';

const getImageUrl = (handle: string, mimetype: string) =>
  imageUrl({
    handle,
    mimetype,
    transform: `resize=width:2096,fit:max/quality=value:70`
  });

const StyledInputField = ({
  name,
  type,
  label,
  disabled
}: {
  name: string,
  type: string,
  label: string,
  disabled?: boolean
}) => (
  <InputField
    name={name}
    type={type}
    label={label}
    inlineLabel
    disabled={disabled}
    borderRadius="0"
    border="none"
    height="60px"
    pt="16px"
    pb="10px"
  />
);

const StyledSubmitButton = styled(
  ({ isSidebar, ...props }: { isSidebar?: boolean }) => (
    <SubmitButton {...props} />
  )
)`
  padding-left: 40px;
  padding-right: 40px;
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;
  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.turtleGreen} inset;
    background: ${colors.turtleGreen};
  }
  ${props =>
    props.isSidebar &&
    `
    width: 100%;
  `}
`;

type Props = {
  headline: string,
  bgImage: ?ImageData,
  buttonText: string,
  isSidebar?: boolean,
  disabled: boolean,
  isSubmitted: boolean,
  onSubmit: Function,
  qaId?: string,
  preForm?: React.Node
};

const EmailCampaignSignup = ({
  headline,
  bgImage,
  buttonText,
  isSidebar,
  disabled,
  isSubmitted,
  onSubmit,
  qaId,
  preForm
}: Props) => (
  <div>
    <Box
      p={`${isSidebar ? '40px' : '80px'} 20px`}
      mx={!isSidebar && [-gutterWidth / 2, null, -gutterWidth]}
      width={!isSidebar && 'auto !important'}
      borderRadius={[0, 0, 0, !isSidebar && 24]}
      background={`url("${
        bgImage
          ? getImageUrl(bgImage.handle, bgImage.mimetype)
          : '/static/images/blue-loops.svg'
      }") center/cover`}
    >
      <Box maxWidth="960px" m="0 auto">
        <H2 color="white" textAlign="center">
          {headline}
        </H2>
      </Box>
      <Form
        data-qa-id={qaId}
        onSubmit={onSubmit}
        render={() => (
          <>
            {isSubmitted ? (
              <Body color="white" textAlign="center" mt="20px">
                {getText('Thank you for signing up!')}
              </Body>
            ) : (
              <>
                <Box maxWidth="440px" m="40px auto 0">
                  <Box mb="24px">{preForm}</Box>
                  <Box overflow="hidden" borderRadius="4px">
                    <Box display={isSidebar ? 'block' : 'flex'} flexWrap="wrap">
                      <Box
                        width={isSidebar ? '100%' : ['100%', null, '50%', null]}
                        flex="0 0 auto"
                      >
                        <StyledInputField
                          name="firstName"
                          type="text"
                          label={getText('First name')}
                          disabled={disabled}
                        />
                      </Box>
                      <Box
                        width={isSidebar ? '100%' : ['100%', null, '50%', null]}
                        flex="0 0 auto"
                        borderLeft={
                          isSidebar
                            ? '0'
                            : [null, null, `1px solid ${colors.silver}`]
                        }
                        borderTop={
                          isSidebar
                            ? `1px solid ${colors.silver}`
                            : [`1px solid ${colors.silver}`, null, 'none']
                        }
                      >
                        <StyledInputField
                          name="lastName"
                          type="text"
                          label={getText('Last name')}
                          disabled={disabled}
                        />
                      </Box>
                    </Box>
                    <Box borderTop={`1px solid ${colors.silver}`}>
                      <StyledInputField
                        name="email"
                        type="email"
                        label={`${getText('Email')}*`}
                        disabled={disabled}
                      />
                    </Box>
                  </Box>
                </Box>
                <Flex justifyContent="center" mt="40px">
                  <StyledSubmitButton disabled={disabled} isSidebar={isSidebar}>
                    {buttonText}
                  </StyledSubmitButton>
                </Flex>
              </>
            )}
          </>
        )}
      />
    </Box>
  </div>
);

export default EmailCampaignSignup;
